import { getDisplayString, getReferenceString } from "@medplum/core";
import { PCPReferralSection } from "@/components/shared/forms/PCPReferralSection";
import { validatePatientReferral } from "@benchmark/core/adapters";

// Components
import { Stack, Skeleton, Select } from "@mantine/core";
import { FormSection } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { DemographicsSection } from "@/components/shared/forms/DemographicsSection";
import { ContactsSection } from "@/components/shared/forms/ContactsSection";
import { PatientInformationSection } from "@/components/shared/forms/PatientInformationSection";
import { ReferralInformationSection } from "@/components/shared/forms/ReferralInformationSection";
import { ICD10Section } from "@/components/shared/forms/ICD10Section";
import { TelecomSection } from "@/components/shared/forms/TelecomSection";
import { AddressSection } from "@/components/shared/forms/AddressSection";
import { EmergencyContactSection } from "@/components/shared/forms/EmergencyContactSection";
import { CareTeamSection } from "@/components/shared/forms/CareTeamSection";
import { InsuranceSection } from "@/components/shared/forms/InsuranceSection";
import { PatientSummarySection } from "./form-sections/PatientSummarySection";
import { DuplicatePatientAlert } from "./DuplicatePatientAlert";

// Constants
import {
  getLanguageOptions,
  PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS,
} from "@benchmark/core/constants";

// Config
import { CONFIG } from "@/app/config";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import {
  useReferringProviderSearch,
  useBenchmarkStaffSearch,
} from "@/hooks/shared/useFHIROptions";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";
import { useNotifyPatientCreated } from "@/hooks/domain/useNotifyPatientCreated";
import { usePatientDuplicateCheck } from "@/hooks/domain/usePatientDuplicateCheck";

// Types
import type { Patient } from "@medplum/fhirtypes";
import {
  PatientFormData,
  PhoneNumberEntry,
  PatientRelatedPersonContact,
} from "@benchmark/core/types";
import type { FormStep as FormStepType } from "@benchmark/ui/components/atomic";

// Utils
import { loadPatientFormDataContext } from "@benchmark/core/services";
import {
  extractReferralMetadata,
  hasReferralMetadata,
  patientToForm,
  type ReferralFormData,
} from "@benchmark/core/adapters";
import { formValidators } from "@benchmark/ui/utils";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { toE164US } from "@benchmark/core/utils";
import { localCalendarDateTodayISO } from "@benchmark/ui/utils";
import { createPatientFacade } from "@/facades/patient.facade";
import {
  validatePhoneNumbers,
  validatePatientContacts,
  validateInsuranceData,
} from "@benchmark/ui/validators";
import { onboardNewPatient } from "@benchmark/core/workflows";

// Types for DocumentReference creation
import type { DocumentReference } from "@medplum/fhirtypes";

// Interfaces
/**
 * Props for the patient create/edit form.
 * @agentic connections
 */
type PatientFormProps = {
  patient?: Patient;
  /** Initial form values for pre-filling (e.g., from referral extraction) */
  initialValues?: Record<string, unknown>;
  /** Controls duplicate-check input debouncing. Defaults to true. */
  duplicateCheckDebounce?: boolean;
  onSuccess?: (patientId: string) => void;
  onCancel?: () => void;
};

/**
 * Ref methods exposed by PatientForm (e.g., checkProtection).
 * @agentic connections
 */
export type PatientFormRef = {
  checkProtection: () => Promise<boolean>;
};

/** Resource references and fallback labels are not names for referral evidence. @agentic connections */
function readablePractitionerDisplay(
  display: string | undefined,
  reference?: string,
): string | undefined {
  const name = display?.trim();
  return name &&
    name.toLowerCase() !== "unknown" &&
    name !== reference &&
    !/^(?:Practitioner|PractitionerRole)\//.test(name)
    ? name
    : undefined;
}

/**
 * Stepped form for creating or editing patient records.
 * @agentic connections
 */
export const PatientForm = forwardRef<PatientFormRef, PatientFormProps>(
  function PatientForm(
    {
      patient: propPatient,
      initialValues,
      duplicateCheckDebounce = true,
      onSuccess,
      onCancel,
    }: PatientFormProps = {},
    ref,
  ) {
    const medplum = useMedplum();
    const patientFacade = createPatientFacade(medplum);
    const { notifyPatientCreated } = useNotifyPatientCreated();
    const navigate = useNavigate();
    const { patientId } = useParams();

    // Only use URL parameters when component is not being used with explicit props (modal context)
    // If propPatient is provided (even if undefined) or onCancel is provided, we're in modal mode
    const shouldUseUrlParams =
      propPatient === undefined && onCancel === undefined;
    const { data: fetchedPatientResource } = usePatientResource(
      shouldUseUrlParams ? patientId : undefined,
    );
    const existingPatient =
      propPatient || (fetchedPatientResource as Patient | undefined);

    // Get current user's practitioner role for workflow automation and summary author
    const { practitionerRole: currentPractitionerRole } =
      useCurrentPractitionerRole();

    const { isSubmitting, createSubmissionHandler } = useFormSubmission();

    // Track if patient data has been loaded to prevent re-loading
    const hasLoadedPatientData = useRef(false);

    // Search query state for practitioner role selects
    // Pre-fill PCP search with extracted practice name so results appear immediately
    const [pcpSearchQuery, setPcpSearchQuery] = useState(
      () => (initialValues?.["referring-practice"] as string | undefined) ?? "",
    );
    const [lcswSearchQuery, setLcswSearchQuery] = useState("");
    // const [advocateSearchQuery, setAdvocateSearchQuery] = useState("");

    // Reusable validator functions for step validation (using extracted validators)
    const validatePhoneNumbersCallback = useCallback(
      (phoneNumbers: PhoneNumberEntry[] | undefined): string | null => {
        return validatePhoneNumbers(phoneNumbers);
      },
      [],
    );

    const validatePatientContactsCallback = useCallback(
      (contacts: PatientRelatedPersonContact[] | undefined): string | null => {
        return validatePatientContacts(contacts);
      },
      [],
    );

    // Memoize validation to prevent recreation on every render
    const formValidation = useMemo(
      () => ({
        "cocm-consent-date": (_value: unknown, values: PatientFormData) =>
          existingPatient
            ? null
            : (validatePatientReferral(values)["cocm-consent-date"] ?? null),
        "pcp-additional-recommendations": (
          _value: unknown,
          values: PatientFormData,
        ) =>
          validatePatientReferral(values)["pcp-additional-recommendations"] ??
          null,
        "first-name": formValidators.required("First name"),
        "last-name": formValidators.required("Last name"),
        dob: (value: unknown, values: PatientFormData) => {
          // Bypass validation if "Date of Birth Not Available" is checked
          if (values["dob-not-available"] === true) {
            return null;
          }
          // Otherwise, apply normal validation
          const requiredError = formValidators.required("Date of birth")(value);
          if (requiredError) return requiredError;
          const dateError = formValidators.date(
            true,
            "Date of birth",
          )(value as string | undefined);
          if (dateError) return dateError;
          const pastDateError = formValidators.pastDate("Date of birth")(
            value as string | undefined,
          );
          return pastDateError;
        },
        "referral-date": formValidators.combine(
          formValidators.required("Referral date"),
          (value: unknown) =>
            formValidators.date(
              true,
              "Referral date",
            )(value as string | undefined),
        ),
        "primary-care-provider": formValidators.required(
          "Primary care provider",
        ),
        mrn: formValidators.required("MRN"),
        emr: formValidators.required("EMR system"),
        phoneNumbers: validatePhoneNumbersCallback,
        email: formValidators.email(false),
        zip: (value: unknown, values: PatientFormData) => {
          if (values["address-not-available"] === true) {
            return null;
          }
          const hasAnyAddress =
            values.street || values.city || values.state || values.zip;
          if (!hasAnyAddress) {
            return null;
          }
          return formValidators.zipCode(true)(value as string | undefined);
        },
        "emergency-contact-phone": formValidators.phone(false),
        "primary-subscriber-phone": formValidators.phone(false),
        "secondary-subscriber-phone": formValidators.phone(false),
        "patient-contacts": (value: unknown) => {
          const contacts = value as PatientRelatedPersonContact[] | undefined;
          return validatePatientContactsCallback(contacts);
        },
        "benchmark-lcsw": formValidators.required("Primary Advocate"),
      }),
      [
        validatePhoneNumbersCallback,
        validatePatientContactsCallback,
        existingPatient,
      ],
    );

    // Form with comprehensive validation
    const form = useForm<PatientFormData>({
      initialValues: {
        "first-name": "",
        "last-name": "",
        "preferred-name": "",
        dob: "",
        "dob-not-available": false,
        "referral-date": localCalendarDateTodayISO(), // Default to today (local calendar)
        "primary-care-provider": "",
        mrn: "",
        emr: "",
        "preferred-language": "",
        phoneNumbers: [{ value: "", use: "mobile", rank: 1 }],
        email: "",
        street: "",
        "street-2": "",
        city: "",
        state: "",
        zip: "",
        "address-not-available": false,
        "primary-insurance-provider": "",
        "primary-subscriber-id": "",
        "primary-relationship-to-subscriber": "self",
        "primary-subscriber-first-name": "",
        "primary-subscriber-last-name": "",
        "primary-subscriber-dob": "",
        "primary-subscriber-phone": "",
        "has-secondary-insurance": false,
        "secondary-insurance-provider": "",
        "secondary-subscriber-id": "",
        "secondary-relationship-to-subscriber": "self",
        "secondary-subscriber-first-name": "",
        "secondary-subscriber-last-name": "",
        "secondary-subscriber-dob": "",
        "secondary-subscriber-phone": "",
        "emergency-contact-first-name": "",
        "emergency-contact-last-name": "",
        "emergency-contact-phone": "",
        race: "",
        ethnicity: "",
        "veteran-status": false,
        "gender-identity": "",
        pronouns: "",
        "sexual-orientation": [],
        "self-pay": false,
        "benchmark-lcsw": "",
        "benchmark-advocate": "",
        // Default engagement status for new patients (V2)
        "patient-engagement-status":
          PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS.find(
            (o) => o.value === "referred",
          )?.value || "referred",
        "patient-contacts": [],
        "cocm-consent-verified": undefined,
        "cocm-consent-date": "",
        "icd10-codes": [],
        summary: "",
        "_extracted-insurance-provider": "",
        "_extracted-subscriber-id": "",
        "_extracted-diagnosis-codes": "",
      },
      validate: formValidation,
    });

    // Duplicate patient detection for new patients
    const { duplicates, isChecking: isCheckingDuplicates } =
      usePatientDuplicateCheck(
        (form.values["first-name"] as string) || "",
        (form.values["last-name"] as string) || "",
        (form.values.dob as string) || "",
        existingPatient?.id,
        { debounce: duplicateCheckDebounce },
      );

    const handleViewDuplicatePatient = useCallback((patientId: string) => {
      window.open(`/Patient/${patientId}`, "_blank");
    }, []);

    // Referring providers (external PCPs) - filtered by RP role code, not org IDs
    const {
      options: pcpSearchOptions,
      isSearching: searchingReferringProviders,
      error: referringProviderError,
      resources: pcpSearchResults,
    } = useReferringProviderSearch(pcpSearchQuery);

    // Benchmark staff for LCSW assignment
    const {
      options: lcswSearchOptions,
      isSearching: searchingLcsw,
      error: benchmarkLcswError,
      resources: lcswSearchResults,
    } = useBenchmarkStaffSearch(
      lcswSearchQuery,
      CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    );

    // Benchmark staff for Advocate assignment
    // const {
    //   options: advocateSearchOptions,
    //   isSearching: searchingAdvocate,
    //   error: benchmarkAdvocateError,
    //   resources: advocateSearchResults,
    // } = useBenchmarkStaffSearch(
    //   advocateSearchQuery,
    //   CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    // );

    // Build options from form state OR search results
    const referringProviderOptions = useMemo(() => {
      const selectedId = form.values["primary-care-provider"] as
        string | undefined;
      const selectedLabel = form.values["primary-care-provider-display"] as
        string | undefined;
      if (pcpSearchQuery.trim().length >= 2) {
        return pcpSearchOptions.map((option) =>
          option.value === selectedId && selectedLabel
            ? { ...option, label: selectedLabel }
            : option,
        );
      }
      // When not searching, show selected value from form state.
      if (selectedId && selectedLabel) {
        return [{ value: selectedId, label: selectedLabel }];
      }
      return [];
    }, [pcpSearchQuery, pcpSearchOptions, form.values]);

    const benchmarkLcswOptions = useMemo(() => {
      if (lcswSearchQuery.trim().length >= 2) {
        return lcswSearchOptions;
      }
      // When not searching, show selected value from form state
      const selectedId = form.values["benchmark-lcsw"] as string | undefined;
      const selectedLabel = form.values["benchmark-lcsw-display"] as
        string | undefined;
      if (selectedId && selectedLabel) {
        return [{ value: selectedId, label: selectedLabel }];
      }
      return [];
    }, [lcswSearchQuery, lcswSearchOptions, form.values]);

    // const benchmarkAdvocateOptions = useMemo(() => {
    //   if (advocateSearchQuery.trim().length >= 2) {
    //     return advocateSearchOptions;
    //   }
    //   // When not searching, show selected value from form state
    //   const selectedId = form.values["benchmark-advocate"] as
    //     | string
    //     | undefined;
    //   const selectedLabel = form.values["benchmark-advocate-display"] as
    //     | string
    //     | undefined;
    //   if (selectedId && selectedLabel) {
    //     return [{ value: selectedId, label: selectedLabel }];
    //   }
    //   return [];
    // }, [advocateSearchQuery, advocateSearchOptions, form.values]);

    // Capture a readable referrer name before immutable referral evidence is saved.
    const handlePcpSelect = useCallback(
      async (value: string | null) => {
        form.setFieldValue("primary-care-provider", value || "");
        form.setFieldValue("primary-care-provider-display", "");
        if (!value) {
          form.setFieldValue("primary-care-provider-organization-ref", "");
          form.setFieldValue("primary-care-provider-organization-display", "");
          return;
        }

        const selected = pcpSearchResults.find((role) => role.id === value);
        if (selected?.organization) {
          form.setFieldValue(
            "primary-care-provider-organization-ref",
            selected.organization.reference || "",
          );
          form.setFieldValue(
            "primary-care-provider-organization-display",
            selected.organization.display || "",
          );
        }
        // Role codes describe a provider's function, not the practitioner's name.
        let display = readablePractitionerDisplay(
          selected?.practitioner?.display,
          selected?.practitioner?.reference,
        );
        if (display === value) display = undefined;
        if (!display && selected?.practitioner?.reference) {
          try {
            const practitioner = await medplum.readReference(
              selected.practitioner,
            );
            if (practitioner.resourceType === "Practitioner") {
              const name = practitioner.name?.find(
                (name) =>
                  name.text?.trim() ||
                  name.family?.trim() ||
                  name.given?.some((given) => given.trim()),
              );
              if (name)
                display = getDisplayString({ ...practitioner, name: [name] });
            }
          } catch {
            // A failed directory lookup must not expose diagnostics or save an ID as a name.
          }
        }
        // A slower lookup must not overwrite a more recently selected provider.
        if (form.getValues()["primary-care-provider"] !== value) return;
        form.setFieldValue("primary-care-provider-display", display || "");
        if (display) {
          form.clearFieldError("primary-care-provider");
        } else {
          form.setFieldError(
            "primary-care-provider",
            "Select a primary care provider with a recorded name.",
          );
        }
      },
      [form, pcpSearchResults, medplum],
    );

    // Handler to update both ID and display name for LCSW
    const handleLcswSelect = useCallback(
      (value: string | null) => {
        form.setFieldValue("benchmark-lcsw", value || "");
        if (value) {
          const selected = lcswSearchResults.find((r) => r.id === value);
          if (selected) {
            form.setFieldValue(
              "benchmark-lcsw-display",
              selected.practitioner?.display ||
                selected.code?.[0]?.coding?.[0]?.display ||
                "",
            );
          }
        } else {
          form.setFieldValue("benchmark-lcsw-display", "");
        }
      },
      [form, lcswSearchResults],
    );

    // Handler to update both ID and display name for Advocate
    // const handleAdvocateSelect = useCallback(
    //   (value: string | null) => {
    //     form.setFieldValue("benchmark-advocate", value || "");
    //     if (value) {
    //       const selected = advocateSearchResults.find((r) => r.id === value);
    //       if (selected) {
    //         form.setFieldValue(
    //           "benchmark-advocate-display",
    //           selected.practitioner?.display ||
    //             selected.code?.[0]?.coding?.[0]?.display ||
    //             "",
    //         );
    //       }
    //     } else {
    //       form.setFieldValue("benchmark-advocate-display", "");
    //     }
    //   },
    //   [form, advocateSearchResults],
    // );

    // Combined loading state for form disabling
    const loadingPartnerPractitioners = searchingReferringProviders;
    const loadingBenchmarkPractitioners = searchingLcsw;
    // const loadingBenchmarkPractitioners = searchingLcsw || searchingAdvocate;
    const partnerPractitionerRolesError = referringProviderError;
    const benchmarkPractitionerRolesError = benchmarkLcswError;
    // benchmarkLcswError || benchmarkAdvocateError;

    // Handle errors from the hooks
    useEffect(() => {
      if (partnerPractitionerRolesError) {
        // FHIR errors can contain patient data; keep this diagnostic fixed.
        console.error("Error fetching partner practitioner roles.");
        showErrorNotification(
          "Error loading partner practitioners. Please refresh the page.",
        );
      }
      if (benchmarkPractitionerRolesError) {
        // FHIR errors can contain patient data; keep this diagnostic fixed.
        console.error("Error fetching benchmark practitioner roles.");
        showErrorNotification(
          "Error loading benchmark practitioners. Please refresh the page.",
        );
      }
    }, [partnerPractitionerRolesError, benchmarkPractitionerRolesError]);

    // Get form protection for modal integration
    const { checkProtection, markAsClean, ConfirmationModal } =
      useFormProtection(form, {
        enabled: true,
        blockNavigation: true,
        confirmationMessage:
          "You have unsaved changes. Are you sure you want to close without saving?",
      });

    // Expose form protection to parent component (modal) through ref
    useImperativeHandle(ref, () => {
      return {
        checkProtection,
      };
    }, [checkProtection]);

    // Memoize form steps to prevent recreation on every render
    const formSteps: FormStepType<PatientFormData>[] = useMemo(
      () => [
        {
          id: "member",
          title: "Member",
          subtitle:
            "Enter the member's basic information, referral details, and demographics",
          requiredFields: [
            "first-name",
            "last-name",
            "primary-care-provider",
            "referral-date",
            "emr",
            "mrn",
          ],
          validate: (values: PatientFormData) => {
            const errors: Record<string, string> = {};

            // Validate required fields
            const firstNameError = formValidators.required("First name")(
              values["first-name"],
            );
            if (firstNameError) errors["first-name"] = firstNameError;

            const lastNameError = formValidators.required("Last name")(
              values["last-name"],
            );
            if (lastNameError) errors["last-name"] = lastNameError;

            const providerError = formValidators.required(
              "Primary care provider",
            )(values["primary-care-provider"]);
            if (providerError) errors["primary-care-provider"] = providerError;

            const referralDateError = formValidators.combine(
              formValidators.required("Referral date"),
              (value: unknown) =>
                formValidators.date(
                  true,
                  "Referral date",
                )(value as string | undefined),
            )(values["referral-date"]);
            if (referralDateError) errors["referral-date"] = referralDateError;

            if (!existingPatient)
              Object.assign(errors, validatePatientReferral(values));

            const emrError = formValidators.required("EMR system")(values.emr);
            if (emrError) errors.emr = emrError;

            const mrnError = formValidators.required("MRN")(values.mrn);
            if (mrnError) errors.mrn = mrnError;

            // Only require DOB if "Date of Birth Not Available" is not checked
            if (!values["dob-not-available"]) {
              const dobRequiredError = formValidators.required("Date of birth")(
                values.dob,
              );
              if (dobRequiredError) {
                errors.dob = dobRequiredError;
              } else {
                const dobDateError = formValidators.date(
                  true,
                  "Date of birth",
                )(values.dob);
                if (dobDateError) {
                  errors.dob = dobDateError;
                } else {
                  const dobPastError = formValidators.pastDate("Date of birth")(
                    values.dob,
                  );
                  if (dobPastError) {
                    errors.dob = dobPastError;
                  }
                }
              }
            }

            return Object.keys(errors).length > 0 ? errors : null;
          },
        },
        {
          id: "contacts",
          title: "Contact Information",
          subtitle:
            "Enter contact information, an emergency contact, and any additional contacts",
          requiredFields: ["phoneNumbers"],
          validate: (values: PatientFormData) => {
            const errors: Record<string, string> = {};

            // Full phone numbers validation
            const phoneError = validatePhoneNumbersCallback(
              values.phoneNumbers,
            );
            if (phoneError) {
              errors.phoneNumbers = phoneError;
            }

            // Address validation (skip when address not available)
            if (!values["address-not-available"]) {
              const hasAnyAddress =
                values.street || values.city || values.state || values.zip;
              if (!hasAnyAddress) {
                errors.street =
                  'Please enter an address or check "Address Not Available"';
              } else {
                const zipError = formValidators.zipCode(true)(values.zip);
                if (zipError) {
                  errors.zip = zipError;
                }
              }
            }

            // Email validation if provided
            if (values.email) {
              const emailError = formValidators.email(false)(values.email);
              if (emailError) {
                errors.email = emailError;
              }
            }

            // Emergency contact phone validation if provided
            if (values["emergency-contact-phone"]) {
              const emergencyPhoneError = formValidators.phone(false)(
                values["emergency-contact-phone"],
              );
              if (emergencyPhoneError) {
                errors["emergency-contact-phone"] = emergencyPhoneError;
              }
            }

            // Additional contacts validation
            const contactsError = validatePatientContactsCallback(
              values["patient-contacts"],
            );
            if (contactsError) {
              errors["patient-contacts"] = contactsError;
            }

            return Object.keys(errors).length > 0 ? errors : null;
          },
        },
        /**
         * Field values here map to FHIR Coverage via patient create/update workflows;
         * primary is persisted with Coverage.order = 1 and secondary with order = 2.
         */
        {
          id: "insurance",
          title: "Insurance",
          subtitle: "Enter insurance information",
          optional: true,
          validate: (values: PatientFormData) => {
            return validateInsuranceData(values);
          },
        },
        {
          id: "care-team",
          title: "Care Team",
          subtitle: "Assign Benchmark care team members",
          requiredFields: ["benchmark-lcsw"],
          validate: (values: PatientFormData) => {
            const errors: Record<string, string> = {};

            // LCSW is required
            const lcswError = formValidators.required("Primary Advocate")(
              values["benchmark-lcsw"],
            );
            if (lcswError) {
              errors["benchmark-lcsw"] = lcswError;
            }

            return Object.keys(errors).length > 0 ? errors : null;
          },
        },
      ],
      [
        validatePhoneNumbersCallback,
        validatePatientContactsCallback,
        existingPatient,
      ],
    );

    // Track if initial values have been applied (for referral pre-fill)
    const hasAppliedInitialValues = useRef(false);

    // Populate form with existing patient data
    // Use patient ID as key to prevent re-loading when form object reference changes
    const patientIdRef = useRef<string | undefined>(existingPatient?.id);
    useEffect(() => {
      // Only load if we have a patient and haven't loaded this patient yet
      if (
        existingPatient &&
        existingPatient.id &&
        existingPatient.id !== patientIdRef.current
      ) {
        patientIdRef.current = existingPatient.id;
        hasLoadedPatientData.current = false;
      }

      if (existingPatient && !hasLoadedPatientData.current) {
        const loadPatientData = async () => {
          try {
            hasLoadedPatientData.current = true;
            const context = await loadPatientFormDataContext(
              medplum,
              existingPatient,
            );
            const formData = patientToForm(context);
            form.setValues(formData);

            // When editing: if DOB is undefined/empty, auto-check "Date of Birth Not Available"
            if (!formData.dob || formData.dob.trim() === "") {
              form.setFieldValue("dob-not-available", true);
            }

            // When editing: if address is empty, auto-check "Address Not Available"
            const hasAddress =
              formData.street ||
              formData.city ||
              formData.state ||
              formData.zip;
            if (!hasAddress) {
              form.setFieldValue("address-not-available", true);
            }
          } catch {
            // Error converting patient to form data - form will use default values
            hasLoadedPatientData.current = false;
          }
        };
        loadPatientData();
      }
      // Only depend on existingPatient and medplum - form.setValues is stable
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingPatient?.id, medplum]);

    // Apply initial values for pre-filling (e.g., from referral extraction)
    // This runs once when initialValues are provided for a new patient
    useEffect(() => {
      if (
        initialValues &&
        !existingPatient &&
        !hasAppliedInitialValues.current
      ) {
        hasAppliedInitialValues.current = true;
        // Merge initial values with default form values
        const mergedValues = { ...form.values };
        Object.entries(initialValues).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            (mergedValues as Record<string, unknown>)[key] = value;
          }
        });
        form.setValues(mergedValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues, existingPatient]);

    const handleSubmit = async (values: PatientFormData) => {
      try {
        // Enhance form data with display names from our already-loaded practitioner roles
        const enhancedValues = { ...values };

        // Phone numbers are normalized in upsertPatientFromFormData
        // Normalize other phone fields to E.164 (US)
        if (enhancedValues["emergency-contact-phone"]) {
          enhancedValues["emergency-contact-phone"] = toE164US(
            enhancedValues["emergency-contact-phone"],
          );
        }
        if (enhancedValues["primary-subscriber-phone"]) {
          enhancedValues["primary-subscriber-phone"] = toE164US(
            enhancedValues["primary-subscriber-phone"],
          );
        }
        if (enhancedValues["secondary-subscriber-phone"]) {
          enhancedValues["secondary-subscriber-phone"] = toE164US(
            enhancedValues["secondary-subscriber-phone"],
          );
        }

        // Normalize contact phone numbers
        if (enhancedValues["patient-contacts"]) {
          enhancedValues["patient-contacts"] = enhancedValues[
            "patient-contacts"
          ].map((contact) => ({
            ...contact,
            phone: contact.phone ? toE164US(contact.phone) : contact.phone,
          }));
        }

        // Display names are already in form state (populated by adapter or selection handlers)
        // No need to extract from selected resources - form state is source of truth

        // Build author reference for summary extension if practitioner role is available
        const profile = medplum.getProfile();
        let authorReference =
          currentPractitionerRole?.id && currentPractitionerRole?.practitioner
            ? {
                reference: `PractitionerRole/${currentPractitionerRole.id}`,
                display:
                  currentPractitionerRole.practitioner.display ?? "Unknown",
              }
            : profile
              ? {
                  reference: getReferenceString(profile),
                  display: getDisplayString(profile),
                }
              : undefined;
        if (!existingPatient) {
          const profileReference = profile && getReferenceString(profile);
          const profileName = profile?.name?.find(
            (name) =>
              name.text?.trim() ||
              name.family?.trim() ||
              name.given?.some((given) => given.trim()),
          );
          const profileDisplay = readablePractitionerDisplay(
            profile && profileName
              ? getDisplayString({ ...profile, name: [profileName] })
              : undefined,
            profileReference,
          );
          const authorRole =
            currentPractitionerRole?.id && currentPractitionerRole.practitioner;
          const display = authorRole
            ? profileReference && authorRole.reference === profileReference
              ? (readablePractitionerDisplay(
                  authorRole.display,
                  authorRole.reference,
                ) ?? profileDisplay)
              : undefined
            : profileDisplay;
          if (!authorReference || !display)
            throw new Error(
              "Your staff profile could not be loaded. Please sign in again before creating the patient.",
            );
          authorReference = { ...authorReference, display };
        }

        // Use facade for both creates and updates
        const patient = existingPatient
          ? await patientFacade.update({
              formData: enhancedValues,
              existingPatient,
              authorReference,
            })
          : await patientFacade.create(enhancedValues, authorReference);

        // Send Google Chat notification for new patients (fire and forget)
        if (!existingPatient && patient.id) {
          notifyPatientCreated(patient.id, {
            source: "connections",
            practice: values["referring-practice"],
            referringPractitioner: values["referring-practitioner"],
          });
        }

        // Initialize care plan journey for new patients
        if (!existingPatient && currentPractitionerRole) {
          try {
            // Extract email from profile's telecom array
            await onboardNewPatient(medplum, {
              patient,
              practitionerRole: currentPractitionerRole,
            });
          } catch {
            // FHIR errors can contain patient data; keep this diagnostic fixed.
            console.error("Failed to initialize patient care plan journey.");
          }
        }

        // Create DocumentReference for referral PDF if present
        // This links the uploaded referral document to the newly created patient
        const formValuesWithMetadata = values as ReferralFormData;
        if (!existingPatient && hasReferralMetadata(formValuesWithMetadata)) {
          try {
            const referralMetadata = extractReferralMetadata(
              formValuesWithMetadata,
            );
            if (referralMetadata) {
              const patientName = [values["first-name"], values["last-name"]]
                .filter(Boolean)
                .join(" ");

              const documentReference: DocumentReference = {
                resourceType: "DocumentReference",
                status: "current",
                docStatus: "final",
                type: {
                  coding: [
                    {
                      system: "http://loinc.org",
                      code: "57133-1",
                      display: "Referral note",
                    },
                  ],
                  text: "Referral Document",
                },
                category: [
                  {
                    coding: [
                      {
                        system:
                          "http://hl7.org/fhir/us/core/CodeSystem/us-core-documentreference-category",
                        code: "clinical-note",
                        display: "Clinical Note",
                      },
                    ],
                    text: "Referral",
                  },
                ],
                subject: {
                  reference: `Patient/${patient.id}`,
                  display: patientName || undefined,
                },
                date: new Date().toISOString(),
                description: "Referral document uploaded during patient intake",
                content: [
                  {
                    attachment: {
                      contentType: "application/pdf",
                      url: referralMetadata.binaryUrl,
                      title: "Referral PDF",
                    },
                  },
                ],
              };

              await medplum.createResource(documentReference);
            }
          } catch {
            // FHIR errors can contain patient data; keep this diagnostic fixed.
            console.error("Failed to create referral DocumentReference.");
          }
        }

        showSuccessNotification(
          `Patient ${existingPatient ? "updated" : "created"} successfully`,
        );

        // Mark form as clean after successful submission
        markAsClean();

        // Use callback if provided, otherwise navigate
        if (onSuccess) {
          onSuccess(patient.id!);
        } else {
          navigate(`/Patient/${patient.id}`);
        }
      } catch {
        // FHIR errors can contain patient data; keep this diagnostic fixed.
        console.error(
          `Failed to ${existingPatient ? "update" : "create"} patient.`,
        );
        showErrorNotification(
          `Failed to ${existingPatient ? "update" : "create"} patient. Please try again.`,
        );
        // This is the UI event boundary. Retain the form values for an explicit
        // retry; useFormSubmission resets the loading state in its finally block.
      }
    };

    // Handle cancel - protection is handled by modal's onClose handler
    const handleCancel = useCallback(() => {
      if (!onCancel) return;
      onCancel();
    }, [onCancel]);

    if (patientId && !existingPatient) {
      return (
        <Stack gap="md">
          {/* Progress indicator skeleton */}
          <Stack gap="xs">
            <Skeleton height={24} width="30%" />
            <Skeleton height={8} width="100%" />
            <Skeleton height={32} width="100%" />
          </Stack>
          {/* Form content skeleton */}
          <Stack gap="md">
            <Skeleton height={32} width="40%" />
            <Skeleton height={14} width="30%" />
            <Skeleton height={36} />
            <Skeleton height={14} width="25%" />
            <Skeleton height={36} />
            <Skeleton height={14} width="20%" />
            <Skeleton height={36} />
          </Stack>
          {/* Navigation skeleton */}
          <Skeleton height={44} width="200px" />
        </Stack>
      );
    }

    return (
      <>
        <SteppedForm
          form={form}
          steps={formSteps}
          onSubmit={createSubmissionHandler(handleSubmit)}
          isSubmitting={isSubmitting}
          submitText={existingPatient ? "Update Member" : "Create Member"}
          onCancel={handleCancel}
          allowFreeNavigation={!!existingPatient}
        >
          {/* Step 1: Member */}
          <FormStep id="member">
            <PatientInformationSection form={form} />

            {!existingPatient && (
              <DuplicatePatientAlert
                duplicates={duplicates}
                isChecking={isCheckingDuplicates}
                onViewPatient={handleViewDuplicatePatient}
              />
            )}

            <PatientSummarySection form={form} />

            <ReferralInformationSection
              form={form}
              partnerPractitionerRoleOptions={referringProviderOptions}
              loadingPartnerPractitioners={loadingPartnerPractitioners}
              existingPatient={!!existingPatient}
              pcpSearchQuery={pcpSearchQuery}
              onPcpSearchChange={setPcpSearchQuery}
              onPcpSelect={handlePcpSelect}
            />

            {!existingPatient && <PCPReferralSection form={form} />}

            <ICD10Section
              form={form}
              collapsible
              defaultCollapsed={
                !form.values["icd10-codes"] ||
                form.values["icd10-codes"].length === 0
              }
            />

            <DemographicsSection
              form={form}
              title="Demographics"
              subtitle={`${existingPatient ? "Update" : "Add"} member's demographics`}
              collapsible
              defaultCollapsed={
                !form.values.race &&
                !form.values.ethnicity &&
                !form.values["gender-identity"] &&
                !form.values.pronouns?.trim() &&
                (!form.values["sexual-orientation"] ||
                  form.values["sexual-orientation"].length === 0) &&
                !form.values["veteran-status"]
              }
            />
          </FormStep>

          {/* Step 2: Contacts */}
          <FormStep id="contacts">
            <FormSection title="Preferred Language">
              <FormField label="Preferred Language">
                <Select
                  placeholder="Select preferred language"
                  data={getLanguageOptions()}
                  searchable
                  clearable
                  {...form.getInputProps("preferred-language")}
                />
              </FormField>
            </FormSection>

            <TelecomSection form={form} />

            <AddressSection form={form} />

            <EmergencyContactSection form={form} />

            <ContactsSection
              form={form}
              collapsible
              defaultCollapsed={
                !form.values["patient-contacts"] ||
                form.values["patient-contacts"].length === 0
              }
            />
          </FormStep>

          {/* Step 3: Insurance — Coverage.order 1/2 set in buildCreatePatientBundle + updatePatientCoverage */}
          <FormStep id="insurance">
            <InsuranceSection form={form} />
          </FormStep>

          {/* Step 4: Care Team */}
          <FormStep id="care-team">
            <CareTeamSection
              form={form}
              benchmarkPractitionerRoleOptions={benchmarkLcswOptions}
              loadingBenchmarkPractitioners={loadingBenchmarkPractitioners}
              lcswSearchQuery={lcswSearchQuery}
              onLcswSearchChange={setLcswSearchQuery}
              onLcswSelect={handleLcswSelect}
            />
          </FormStep>
        </SteppedForm>

        <ConfirmationModal />
      </>
    );
  },
);
